import { api } from './api';
import type { UserSummaryResponse, EffectivePermissionsResponse, Role, Department, DirectPermission } from '@apptypes/index';

export const usersService = {
  list: (params?: { page?: number; size?: number }) =>
    api.get<UserSummaryResponse[]>('/admin/users', { page: params?.page ?? 0, size: params?.size ?? 200 } as Record<string, unknown>),

  get: (userId: string) =>
    api.get<UserSummaryResponse>(`/admin/users/${userId}`),

  create: (data: {
    phone: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
    cheKitStaff?: boolean;
    customer?: boolean;
  }) => api.post<UserSummaryResponse>('/admin/users', data),

  suspend: (userId: string, reason?: string) =>
    api.post(`/admin/users/${userId}/suspend`, { reason }),

  activate: (userId: string) =>
    api.post(`/admin/users/${userId}/activate`),

  update: (userId: string, data: {
    firstName?: string; lastName?: string; email?: string;
    phone?: string; nationalId?: string; gender?: string;
  }) => api.patch<UserSummaryResponse>(`/admin/users/${userId}`, data),

  /** Permanently delete (soft-delete + anonymise; history is kept). */
  remove: (userId: string) =>
    api.delete(`/admin/users/${userId}`),

  // ── Effective permissions (resolved from dept + role + direct) ──────────────

  getEffectivePermissions: (userId: string, businessId?: string, branchId?: string) =>
    api.get<EffectivePermissionsResponse>(`/admin/users/${userId}/effective-permissions`, {
      ...(businessId && { businessId }),
      ...(branchId && { branchId }),
    } as Record<string, unknown>),

  // ── Chekit-level roles ──────────────────────────────────────────────────────

  getRoles: (userId: string) =>
    api.get<Role[]>(`/admin/users/${userId}/roles`),

  assignRole: (userId: string, roleId: string) =>
    api.post(`/admin/users/${userId}/roles`, { roleId }),

  removeRole: (userId: string, roleId: string) =>
    api.delete(`/admin/users/${userId}/roles/${roleId}`),

  // ── Chekit-level departments ────────────────────────────────────────────────

  getDepartments: (userId: string) =>
    api.get<Department[]>(`/admin/users/${userId}/departments`),

  addToDepartment: (userId: string, departmentId: string) =>
    api.post(`/admin/users/${userId}/departments`, { departmentId }),

  removeFromDepartment: (userId: string, departmentId: string) =>
    api.delete(`/admin/users/${userId}/departments/${departmentId}`),

  // ── Direct permission grants/denies ────────────────────────────────────────

  getDirectPermissions: (userId: string) =>
    api.get<DirectPermission[]>(`/admin/users/${userId}/direct-permissions`),

  grantPermission: (userId: string, permissionId: string, deny = false) =>
    api.post(`/admin/users/${userId}/direct-permissions`, { permissionId, deny }),

  revokeDirectPermission: (userId: string, permissionId: string) =>
    api.delete(`/admin/users/${userId}/direct-permissions/${permissionId}`),
};
